import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  TextareaAutosize,
  Typography,
  Chip,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { Autocomplete } from '@material-ui/lab';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Page, SelectImageButton } from 'src/components';
import { DatePickerRangeComponentV2 } from 'src/components';
import { useFormik } from 'formik';
import useResolution from 'src/hooks/useResolution';
import HelpIcon from '@material-ui/icons/Help';
import { useVouchers } from 'src/hooks/useVouchers';
import moment from 'moment';
import { CreateVoucherRequest } from 'src/redux/slices/voucher/types';
import { constants, voucherModes } from 'src/redux/slices/voucher';
import { usePermissions, useSnackBar } from 'src/hooks';
import { VoucherScopeSection } from './components/VoucherScopeSection';
import { EcommUserListDialog } from './components/EcommUserListDialog';
import {
  convertImageFileToBase64,
  decimalToPercent,
  percentToDecimal
} from 'src/utils';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import { SpecialField } from './components/SpecialField';
import { isEmpty } from 'lodash';

const { actions: optionActions } = slices.options;

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100%',
    padding: theme.spacing(1)
  },
  container: {
    display: 'flex',
    gap: '10px'
  },
  button: {
    padding: '0px',
    overflow: 'hidden'
  }
}));

const CreateVoucherView = () => {
  const { canCreateVoucher } = usePermissions();
  const classes = useStyles();
  const { lg, sm, xl } = useResolution();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const location = useLocation();

  const formMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('mode') || 'default';
  }, [location.search]);

  const { generateVoucherCode, createVoucher, isCreateLoading } = useVouchers({
    isGeneric: voucherModes?.[formMode]?.isGeneric
  });

  const [openAffiliateList, setOpenAffiliateList] = useState<boolean>(false);
  const [isAppNamesLoading, setIsAppNamesLoading] = useState<boolean>(false);
  const [appNameOptions, setAppNameOptions] = useState<string[]>([]);
  const [selectedAppNames, setSelectedAppNames] = useState<string[]>([]);
  const [affiliateName, setAffiliateName] = useState<string>('');

  // intended empty band aid fix for memory leak

  const { purchaseIcon, shippingIcon } = constants;

  const discountTypeOptions = [
    { label: 'Fix Amount', value: 'amount' },
    { label: 'By Percentage', value: 'percentage' }
  ];

  const initialValues: CreateVoucherRequest = {
    voucher_icon_url: purchaseIcon,
    is_generic: voucherModes?.[formMode].isGeneric,
    quantity: voucherModes?.[formMode].quantityDefault
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      const code = values.code;
      if (!values.is_generic && (!code || code.length < 10)) {
        formik.setFieldError('code', 'Code must be at least 10 characters');
        formik.setFieldError('usage_limit', '_');
        return;
      } else {
        const isCreated = await createVoucher(values);

        if (isCreated) {
          formik.resetForm();
          setSelectedAppNames([]);
          setAffiliateName('');
        }
      }
    }
  });

  const isPercentage = formik.values.discount_type === 'percentage';

  const onClickGenerate = useCallback(async () => {
    const code = await generateVoucherCode();

    if (code) {
      formik.setFieldValue('code', code);
    }
  }, [formik, generateVoucherCode]);

  const onImageSelected = async (img: File[]) => {
    if (img && img?.length > 0) {
      const firstFile = img[0];
      const imgInBase64: any = await convertImageFileToBase64(firstFile);
      formik.setFieldValue('banner_url', imgInBase64);
    }
  };

  const handleAppNameOptions = async () => {
    setIsAppNamesLoading(true);
    const res = unwrapResult(await dispatch(optionActions.getAppNamesThunk()));
    if (res.success) {
      setAppNameOptions(res.originalData.data.split(','));
    } else {
      snackbar.show({
        severity: 'error',
        message:
          'Failed to get app name options, please refresh page or contact support'
      });
    }
    setIsAppNamesLoading(false);
  };

  const handleVocherSpecs = async (values: any) => {
    if (values) {
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'VOUCHER_SPECS_CREATED',
            payload: values
          },
          window.location.origin
        );
      }

      window.close();
    }
  };

  useEffect(() => {
    handleAppNameOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    formik.setFieldValue('app_name', selectedAppNames.join(','));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppNames]);

  return (
    <Page className={classes.root} title={voucherModes?.[formMode]?.title}>
      <Box p={1}>
        <form>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography className="heading" variant="h4" gutterBottom>
              {voucherModes?.[formMode]?.title}
            </Typography>
            {voucherModes?.[formMode]?.backButton ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate('/app/vouchers')}
              >
                Back
              </Button>
            ) : null}
          </Box>

          {/*  main container */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{
              marginBottom: '1em',
              alignItems: 'end',
              rowGap: '.5em'
            }}
          >
            {/* voucher type */}
            <Grid item container xs={12} md={12}>
              <Grid item container xs={12} xl={12} md={12}>
                <Grid item xs={12} md={12}>
                  <Typography variant="h6">Voucher Type:</Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={12} style={{ display: 'flex' }}>
                <Button
                  className={classes.button}
                  color="primary"
                  onClick={() =>
                    formik.setFieldValue('voucher_icon_url', purchaseIcon)
                  }
                  style={{
                    opacity:
                      formik.values.voucher_icon_url === purchaseIcon ? 1 : 0.5,
                    filter:
                      formik.values.voucher_icon_url === purchaseIcon
                        ? 'none'
                        : 'grayscale(100%) brightness(0.7)',
                    pointerEvents: 'auto',
                    marginRight: '.2em'
                  }}
                >
                  <img
                    src="https://devs-api-assets.s3.ap-east-1.amazonaws.com/assets/voucher/type_icon/purchase_discount.png"
                    alt="Purchase"
                    style={{ width: 79.29, height: 66.69 }}
                  />
                </Button>

                <Button
                  className={classes.button}
                  onClick={() =>
                    formik.setFieldValue('voucher_icon_url', shippingIcon)
                  }
                  style={{
                    opacity:
                      formik.values.voucher_icon_url === shippingIcon ? 1 : 0.5,
                    filter:
                      formik.values.voucher_icon_url === shippingIcon
                        ? 'none'
                        : 'grayscale(100%) brightness(0.5)',
                    pointerEvents: 'auto'
                  }}
                >
                  <img
                    src="https://devs-api-assets.s3.ap-east-1.amazonaws.com/assets/voucher/type_icon/shipping_discount.png"
                    alt="Shipping"
                    style={{ width: 79.29, height: 66.69 }}
                  />
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column">
                <Typography>Voucher Name:</Typography>
                <TextField
                  name="name"
                  value={formik?.values?.name}
                  onChange={formik.handleChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>

            {/* app name */}
            <Grid
              item
              xs={12}
              md={6}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <Typography>Apps where this voucher can be used:</Typography>
              <Autocomplete
                id="app-name"
                multiple
                disabled={isAppNamesLoading}
                options={appNameOptions}
                value={selectedAppNames}
                renderTags={(value: any, getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      key={`${option}${index}`}
                      variant="outlined"
                      style={{ fontSize: '.8em', height: '2em' }}
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                onChange={(_, newValue: any) => {
                  setSelectedAppNames(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    fullWidth
                    name="app_name"
                    type="multiple"
                  />
                )}
              />
            </Grid>

            {/* voucher code | quantity field */}
            <SpecialField
              formik={formik}
              variant={voucherModes?.[formMode]?.specialField}
              onClickGenerate={onClickGenerate}
            />

            <Grid item xs={12} md={6}>
              <Typography>Maximum number of uses:</Typography>
              <TextField
                name="usage_limit"
                value={formik?.values?.usage_limit}
                onChange={(e) => {
                  const limit = e.target.value;
                  const numbersOnlyRegex = /^$|^[1-9][0-9]*$/;
                  if (numbersOnlyRegex.test(limit)) {
                    formik.setFieldValue('usage_limit', limit ? +limit : '');
                  }
                }}
                inputProps={{
                  min: 1, // Prevent negative values
                  step: 1 // Ensure whole numbers only (no decimals)
                }}
                fullWidth
                size="small"
                variant="outlined"
              />
              {formik.touched.code && formik.errors.code && (
                <Typography
                  color="error"
                  variant="caption"
                  style={{ marginTop: 0.5 }}
                >
                  {formik.errors.usage_limit || ' '}
                </Typography>
              )}
            </Grid>

            {/* description */}
            <Grid
              item
              xs={12}
              md={12}
              style={{ ...(xl && !lg && { marginTop: '-1em' }) }}
            >
              <Box display="flex" flexDirection="column">
                <Typography>Description</Typography>

                <TextareaAutosize
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  placeholder="Voucher Description"
                  style={{
                    fontFamily: 'roboto',
                    fontSize: '.9em',
                    width: '100%',
                    height: '5em',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: '10px 12px'
                  }}
                />
              </Box>
            </Grid>
            {/* {formMode ? null : ( */}
            {voucherModes?.[formMode]?.bannerField ? (
              <Grid item xs={12}>
                {formik.values.banner_url && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      alt="banner"
                      style={{
                        width: sm ? 300 : 500,
                        height: undefined,
                        marginBottom: '1rem'
                      }}
                      src={formik.values.banner_url}
                    />
                  </div>
                )}
                <SelectImageButton
                  containerStyle={{ width: '100%' }}
                  title="Select/Update Voucher Banner"
                  helperText="Size Up to 3MB and only jpeg, jpg and png is allowed"
                  multiple={false}
                  onImageSelected={(e) => onImageSelected(e)}
                />
              </Grid>
            ) : null}
          </Grid>

          <Box mb={2}>
            <Grid
              container
              alignItems="center"
              style={{
                alignItems: 'end',
                columnGap: '1em',
                rowGap: '1em'
              }}
            >
              <Grid item xs={12} sm={isPercentage ? 12 : 4} lg xl>
                <Typography>Discount Type:</Typography>
                <Autocomplete
                  options={discountTypeOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    discountTypeOptions.find(
                      (opt) => opt.value === formik.values.discount_type
                    ) || null
                  }
                  onChange={(e, val) => {
                    formik.setFieldValue('discount_type', val?.value || '');
                    formik.setFieldValue('amount', 0);
                    formik.setFieldValue('min_spend', 0);
                    formik.setFieldValue('cap_amount', 0);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {formik.values.discount_type === 'percentage' ? (
                <>
                  <Grid item xs={12} lg xl>
                    <Typography>Percentage Off:</Typography>
                    <TextField
                      type="number"
                      value={
                        formik.values.amount === null
                          ? ''
                          : formik.values.amount
                      }
                      name=""
                      fullWidth
                      variant="outlined"
                      onChange={(e) => {
                        let value =
                          e.target.value === '' ? null : +e.target.value;
                        if (value !== null && value > 100) value = 100; // Clamp max to 100
                        formik.setFieldValue('amount', value);
                      }}
                      placeholder="Enter Percentage"
                      size="small"
                      inputProps={{
                        min: 0, // Prevent negative values
                        max: 100, // Set a max limit
                        step: 1 // Ensure whole numbers only (no decimals)
                      }}
                      InputProps={{
                        endAdornment: (
                          <Box
                            style={{
                              display: 'flex',
                              justifyContent: 'end',
                              alignItems: 'center',
                              borderLeft: '1px solid rgba(0,0,0,0.2)',
                              width: '1.5em',
                              height: '2.25em'
                            }}
                          >
                            <Typography style={{ fontSize: '1em' }}>
                              %
                            </Typography>
                          </Box>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={10} sm={11} lg xl>
                    <Typography>Percentage Discount Cap:</Typography>
                    <TextField
                      fullWidth
                      value={formik.values.cap_amount}
                      name="cap_amount"
                      onChange={formik.handleChange}
                      type="number"
                      variant="outlined"
                      placeholder="Enter Discount Cap"
                      size="small"
                    />
                  </Grid>
                  <Grid item style={{ height: '2em' }}>
                    <Tooltip title="The maximum discount amount value allowed for this percentage type voucher">
                      <HelpIcon style={{ color: '#373F50' }} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} lg xl>
                    <Typography>Min. Spend:</Typography>
                    <TextField
                      name="min_spend"
                      placeholder="Enter Amount"
                      InputProps={{
                        startAdornment: <p style={{ marginRight: '1em' }}>₱</p>
                      }}
                      value={formik.values.min_spend}
                      onChange={(e) =>
                        formik.setFieldValue('min_spend', e.target.value)
                      }
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm lg container>
                  <Grid item xs={6} sm style={{ paddingRight: '1em' }}>
                    <Typography>Amount Off:</Typography>
                    <TextField
                      name="amount"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs sm lg={isPercentage ? 4 : 6}>
                    <Typography>Min. Spend:</Typography>
                    <TextField
                      name="min_spend"
                      value={formik.values.min_spend}
                      onChange={formik.handleChange}
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              )}

              {voucherModes?.[formMode]?.affiliateField ? (
                <Grid item sm={12} lg>
                  <Typography>Affiliate User:</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Choose Affiliate User"
                    size="small"
                    value={affiliateName}
                    InputProps={{
                      readOnly: true,
                      endAdornment: affiliateName && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAffiliateName('');
                              formik.setFieldValue('affiliate_user_id', null);

                              formik.setFieldValue('affiliate_incentive', null);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onClick={() => setOpenAffiliateList(true)}
                  />
                </Grid>
              ) : null}

              {voucherModes?.[formMode]?.affiliateField && affiliateName ? (
                <Grid item sm={12} lg>
                  <Typography>Affiliate User Incentive:</Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={
                      formik.values.affiliate_incentive === null
                        ? ''
                        : Number(
                            decimalToPercent(
                              formik.values.affiliate_incentive
                            )?.toFixed(3)
                          )
                    }
                    variant="outlined"
                    size="small"
                    disabled={isEmpty(affiliateName)}
                    onChange={(e) => {
                      let value = e.target.value === '' ? 0 : +e.target.value;

                      const regex = /^\d*\.?\d{0,3}$/;

                      if (regex.test(value.toString())) {
                        if (value > 100) value = 100;

                        const decimal = percentToDecimal(value);

                        formik.setFieldValue(
                          'affiliate_incentive',
                          parseFloat(decimal.toFixed(5))
                        );
                      }
                    }}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 1
                    }}
                    InputProps={{
                      endAdornment: (
                        <Box
                          style={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            borderLeft: '1px solid rgba(0,0,0,0.2)',
                            width: '1.5em',
                            height: '2.25em'
                          }}
                        >
                          <Typography style={{ fontSize: '1em' }}>%</Typography>
                        </Box>
                      )
                    }}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          <Box
            mb={2}
            justifyItems={'center'}
            style={{
              ...(!lg && {
                padding: '0 20em'
              })
            }}
          >
            <DatePickerRangeComponentV2
              fromDateValue={formik.values.start_date}
              toDateValue={formik.values.end_date}
              onChangeToDate={(date) => {
                const formattedDate = moment(date, [
                  'MMMM D, YYYY hh:mm:ss A',
                  'YYYY-MM-DD HH:mm:ss'
                ]).format('YYYY-MM-DD HH:mm:ss');
                formik.setFieldValue('end_date', formattedDate);
              }}
              onChangeFromDate={(date) => {
                const formattedDate = moment(date, [
                  'MMMM D, YYYY hh:mm:ss A',
                  'YYYY-MM-DD HH:mm:ss'
                ]).format('YYYY-MM-DD HH:mm:ss');
                formik.setFieldValue('start_date', formattedDate);
                formik.setFieldValue('end_date', null);
              }}
            />
          </Box>

          <Box style={{ marginTop: '2em' }}>
            <VoucherScopeSection
              onScopeChange={(scopes) => {
                formik.setFieldValue('voucher_scopes', scopes ? scopes : null);
              }}
              isCreatePage={true}
              isGeneric={!!voucherModes?.[formMode]?.isGeneric}
              discountAmount={formik.values.amount}
              discountCap={formik.values.cap_amount}
              discountType={formik.values.discount_type}
            />
          </Box>

          <Box
            mt={sm ? 2 : 3}
            style={{
              display: 'flex',
              gap: '1em',
              justifyContent: 'end'
            }}
          >
            {voucherModes?.[formMode]?.createSpecsButton ? (
              <Button
                variant="outlined"
                color="primary"
                style={{
                  ...(!sm && { width: '20em' })
                }}
                fullWidth={sm}
                onClick={
                  () => handleVocherSpecs(formik.values)
                  // navigator.clipboard.writeText(JSON.stringify(formik.values))
                }
              >
                Create Voucher Specs
              </Button>
            ) : null}
            {canCreateVoucher && voucherModes?.[formMode]?.publishButton ? (
              <Button
                variant="contained"
                color="primary"
                disabled={isCreateLoading}
                style={{
                  ...(!sm && { width: '20em' })
                }}
                fullWidth={sm}
                onClick={() => formik.handleSubmit()}
              >
                Publish Voucher
              </Button>
            ) : null}
          </Box>
        </form>
      </Box>

      <EcommUserListDialog
        isVisible={openAffiliateList}
        handleClose={() => setOpenAffiliateList(false)}
        user_id={formik.values.affiliate_user_id}
        onUserSelect={(user) => {
          const { first_name, last_name } = user;
          formik.setFieldValue('affiliate_user_id', user?.id);

          const fullName = `${first_name} ${last_name}`;
          setAffiliateName(fullName);
          setOpenAffiliateList(false);
        }}
      />
    </Page>
  );
};

export default CreateVoucherView;
